import { CDN_URL, catalogJsonUrl } from '../../config.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { filterLocationsForSfw, getNsfwKeywords, loadNsfwKeywords } from '../../utils/nsfwTags.js';
import { initializePromptAutocomplete } from '../promptAutocomplete.js';
import { initPromptHelpers, insertPromptAtCursor } from './promptHelpersUi.js';
import { openCreateVideoRankedCharacterPicker } from '../ui/rankedCharacterPickerModal.js';
import { openCreateLocationPicker } from '../ui/locationPickerModal.js';
import { loadOutfitsList } from '../../utils/outfitMappings.js';
import { filterOutfitsByGender, sortOutfitsByIdAsc } from './filterOutfitsByGender.js';
import { openPromptImagePicker } from '../ui/promptImagePickerModal.js';
import { HAIR_STYLE_CATEGORIES, HAIR_STYLES, hairStyleImageUrl } from '../data/hairStyles.js';
import { HAIR_COLOR_CATEGORIES, HAIR_COLORS, hairColorImageUrl } from '../data/hairColors.js';
import { EYE_STYLE_CATEGORIES, EYE_STYLES, eyeStyleImageUrl } from '../data/eyeStyles.js';

export const createPromptRefs = () => ({
  characters: {},
  outfits: {},
  locations: {},
});

export const normalizePromptTokenName = (value) => String(value || '').trim().toLowerCase();

export const lookupPromptRefId = (map, token) => {
  if (!map || typeof map !== 'object' || Array.isArray(map)) return null;
  if (map[token] != null && map[token] !== '') return map[token];
  const needle = normalizePromptTokenName(token);
  const match = Object.entries(map).find(([key]) => normalizePromptTokenName(key) === needle);
  return match ? match[1] : null;
};

export const rememberPromptRef = (promptRefs, kind, name, id) => {
  const token = String(name || '').trim();
  if (!token || id == null || id === '') return;
  const bucket = promptRefs?.[kind];
  if (!bucket || typeof bucket !== 'object' || Array.isArray(bucket)) return;
  bucket[token] = String(id);
};

export const isLinkedBraceToken = (promptRefs, name) => (
  lookupPromptRefId(promptRefs?.characters, name) != null
  || lookupPromptRefId(promptRefs?.outfits, name) != null
  || lookupPromptRefId(promptRefs?.locations, name) != null
);

export const extractBraceTokenNames = (prompt) => {
  const tokens = [];
  const re = /\{([^{}]+)\}/g;
  let match = re.exec(prompt || '');
  while (match) {
    const name = String(match[1] || '').trim();
    if (name && !tokens.includes(name)) tokens.push(name);
    match = re.exec(prompt || '');
  }
  return tokens;
};

export const splitOutfitPairToken = (token) => {
  const text = String(token || '').trim();
  const index = text.indexOf(':');
  if (index <= 0 || index >= text.length - 1) return null;
  const characterName = text.slice(0, index).trim();
  const outfitName = text.slice(index + 1).trim();
  if (!characterName || !outfitName) return null;
  return { characterName, outfitName };
};

export const listPlainCharacterTokens = (prompt) => (
  extractBraceTokenNames(prompt).filter((name) => splitOutfitPairToken(name) == null && !name.includes(':'))
);

export const resolveBoundCharacterName = (prompt, lastCharacterName) => {
  const plain = listPlainCharacterTokens(prompt);
  if (!plain.length) return '';
  const preferred = String(lastCharacterName || '').trim();
  if (preferred) {
    const match = plain.find((name) => normalizePromptTokenName(name) === normalizePromptTokenName(preferred));
    if (match) return match;
  }
  return plain[plain.length - 1];
};

export const characterRefStillUsed = (prompt, name) => {
  const needle = normalizePromptTokenName(name);
  return extractBraceTokenNames(prompt).some((token) => {
    if (normalizePromptTokenName(token) === needle) return true;
    const pair = splitOutfitPairToken(token);
    return pair != null && normalizePromptTokenName(pair.characterName) === needle;
  });
};

const braceTokenSet = (prompt) => (
  new Set(extractBraceTokenNames(prompt).map(normalizePromptTokenName))
);

export const promptRefsMatchingPrompt = (promptRefs, prompt) => {
  const next = createPromptRefs();
  if (!promptRefs || typeof promptRefs !== 'object') return next;
  Object.keys(promptRefs.characters || {}).forEach((name) => {
    if (!characterRefStillUsed(prompt, name)) return;
    next.characters[name] = promptRefs.characters[name];
  });
  const tokens = braceTokenSet(prompt);
  ['outfits', 'locations'].forEach((kind) => {
    Object.keys(promptRefs[kind] || {}).forEach((name) => {
      if (!tokens.has(normalizePromptTokenName(name))) return;
      next[kind][name] = promptRefs[kind][name];
    });
  });
  return next;
};

export const prunePromptRefsToPrompt = (promptRefs, prompt) => {
  if (!promptRefs || typeof promptRefs !== 'object') return;
  const matched = promptRefsMatchingPrompt(promptRefs, prompt);
  ['characters', 'outfits', 'locations'].forEach((kind) => {
    const bucket = promptRefs[kind];
    if (!bucket || typeof bucket !== 'object') return;
    Object.keys(bucket).forEach((name) => {
      if (matched[kind][name] == null) delete bucket[name];
    });
  });
};

const isCataloglessOutfit = (item) => {
  if (Number(item?.id) === 0) return true;
  const name = normalizePromptTokenName(item?.label || item?.name);
  return name === 'default' || name === 'default outfit';
};

const insertNamedToken = (textareaEl, promptRefs, kind, name, id) => {
  rememberPromptRef(promptRefs, kind, name, id);
  const suffix = kind === 'characters' || kind === 'outfits' ? ',' : '';
  insertPromptAtCursor(textareaEl, `{${name}}${suffix}`);
};

const outfitImageUrl = (gender, outfitId) => (
  `${CDN_URL}/data/outfits/outfit_images/${gender}/${outfitId}.webp`
);

const openOutfitPicker = async (textareaEl, promptRefs, gender, title, {
  getLastCharacterName,
  onBareDefaultOutfit,
} = {}) => {
  const lang = window.getCurrentLanguage ? window.getCurrentLanguage() : 'en';
  const outfits = sortOutfitsByIdAsc(filterOutfitsByGender(await loadOutfitsList(lang), gender));
  const seen = new Set();
  const items = [];
  outfits.forEach((outfit) => {
    const name = String(outfit?.name || '').trim();
    if (!name || seen.has(name)) return;
    seen.add(name);
    items.push({
      id: outfit.id,
      label: name,
      imageUrl: outfitImageUrl(gender, outfit.id),
    });
  });
  openPromptImagePicker({
    title,
    searchPlaceholder: 'Search outfits...',
    items,
    onSelect: (item) => {
      const characterName = resolveBoundCharacterName(
        textareaEl?.value || '',
        typeof getLastCharacterName === 'function' ? getLastCharacterName() : '',
      );
      const outfitName = String(item?.label || '').trim();
      if (!outfitName) return;
      if (!characterName) {
        if (isCataloglessOutfit(item)) {
          if (typeof onBareDefaultOutfit === 'function') onBareDefaultOutfit();
          return;
        }
        insertNamedToken(textareaEl, promptRefs, 'outfits', outfitName, item.id);
        return;
      }
      insertNamedToken(textareaEl, promptRefs, 'outfits', `${characterName}:${outfitName}`, item.id);
    },
  });
};

const openHairPicker = (textareaEl) => {
  openPromptImagePicker({
    title: 'Hair',
    searchPlaceholder: 'Search hair styles...',
    categories: HAIR_STYLE_CATEGORIES,
    items: HAIR_STYLES.map((style) => ({
      id: style.slug,
      label: style.tag,
      imageUrl: hairStyleImageUrl(style.slug),
      categories: style.categories,
    })),
    onSelect: (item) => insertPromptAtCursor(textareaEl, item.label),
  });
};

const openHairColorPicker = (textareaEl) => {
  openPromptImagePicker({
    title: 'Hair color',
    searchPlaceholder: 'Search hair colors...',
    categories: HAIR_COLOR_CATEGORIES,
    items: HAIR_COLORS.map((color) => ({
      id: color.slug,
      label: color.tag,
      imageUrl: hairColorImageUrl(color.slug),
      categories: color.categories,
    })),
    onSelect: (item) => insertPromptAtCursor(textareaEl, item.label),
  });
};

const openEyePicker = (textareaEl) => {
  openPromptImagePicker({
    title: 'Eye color',
    searchPlaceholder: 'Search eyes...',
    categories: EYE_STYLE_CATEGORIES,
    items: EYE_STYLES.map((style) => ({
      id: style.slug,
      label: style.tag,
      imageUrl: eyeStyleImageUrl(style.slug),
      categories: style.categories,
    })),
    onSelect: (item) => insertPromptAtCursor(textareaEl, item.label),
  });
};

const loadLocationOptions = async () => {
  const lang = window.getCurrentLanguage ? window.getCurrentLanguage() : 'en';
  let data = null;
  try {
    data = await fetchCache.fetchJSON(catalogJsonUrl(`data/loc/${lang}.json`));
  } catch {
    if (lang !== 'en') {
      data = await fetchCache.fetchJSON(catalogJsonUrl('data/loc/en.json'));
    }
  }
  const filtered = filterLocationsForSfw(data);
  if (Array.isArray(filtered)) {
    return filtered
      .map((item) => ({
        value: String(item?.id ?? '').trim(),
        label: String(item?.name ?? '').trim(),
      }))
      .filter((opt) => opt.value && opt.label);
  }
  return Object.entries(filtered || {})
    .map(([id, value]) => ({
      value: String(id).trim(),
      label: String(typeof value === 'object' ? (value?.name || '') : value).trim(),
    }))
    .filter((opt) => opt.value && opt.label && opt.value !== '0');
};

export const initCharacterAppearanceHelpers = ({ extraGroups = {} } = {}) => {
  const textareaEl = document.getElementById('ai-character-prompt');
  initPromptHelpers({
    helperRootId: 'prompt-helper-character',
    pillsRootId: 'prompt-helper-pills-character',
    textareaId: 'ai-character-prompt',
    customGroups: {
      ...extraGroups,
      hair: {
        type: 'action',
        run: () => openHairPicker(textareaEl),
      },
      hair_color: {
        type: 'action',
        run: () => openHairColorPicker(textareaEl),
      },
      eye_color: {
        type: 'action',
        run: () => openEyePicker(textareaEl),
      },
    },
  });
};

export const initStandalonePromptHelpers = ({
  textareaId,
  helperRootId,
  pillsRootId,
  getCharacters,
  promptRefs,
  onCharacterSelected,
  onBareDefaultOutfit,
  onLocationError,
  autocomplete = null,
} = {}) => {
  if (autocomplete?.suggestionType) {
    initializePromptAutocomplete(textareaId, autocomplete.suggestionType, null, autocomplete.options || {})
      .catch((error) => console.warn('init standalone prompt autocomplete failed', error));
  }

  let lastCharacterName = '';
  const textareaEl = document.getElementById(textareaId);
  const forgetUnusedPromptRefs = () => {
    prunePromptRefsToPrompt(promptRefs, textareaEl?.value || '');
  };
  initPromptHelpers({
    helperRootId,
    pillsRootId,
    textareaId,
    customGroups: {
      character: {
        type: 'action',
        run: () => {
          forgetUnusedPromptRefs();
          void openCreateVideoRankedCharacterPicker({
            getCharacters: typeof getCharacters === 'function' ? getCharacters : () => [],
            showCreateCard: false,
            onSelect: (character) => {
              const name = String(character?.name || '').trim();
              if (!name) return;
              lastCharacterName = name;
              insertNamedToken(textareaEl, promptRefs, 'characters', name, character.id);
              if (typeof onCharacterSelected === 'function') onCharacterSelected(character, name);
            },
          });
        },
      },
      outfit_female: {
        type: 'action',
        run: () => {
          forgetUnusedPromptRefs();
          void openOutfitPicker(textareaEl, promptRefs, 'female', 'Female Outfits', {
            getLastCharacterName: () => lastCharacterName,
            onBareDefaultOutfit,
          });
        },
      },
      outfit_male: {
        type: 'action',
        run: () => {
          forgetUnusedPromptRefs();
          void openOutfitPicker(textareaEl, promptRefs, 'male', 'Male Outfits', {
            getLastCharacterName: () => lastCharacterName,
            onBareDefaultOutfit,
          });
        },
      },
      hair: {
        type: 'action',
        run: () => openHairPicker(textareaEl),
      },
      hair_color: {
        type: 'action',
        run: () => openHairColorPicker(textareaEl),
      },
      eye_color: {
        type: 'action',
        run: () => openEyePicker(textareaEl),
      },
      background: {
        type: 'action',
        run: () => {
          forgetUnusedPromptRefs();
          void (async () => {
            try {
              const options = await loadLocationOptions();
              openCreateLocationPicker({
                options,
                allowCreate: false,
                onSelect: (opt) => {
                  const name = String(opt?.label || '').trim();
                  if (!name) return;
                  insertNamedToken(textareaEl, promptRefs, 'locations', name, opt.value);
                },
              });
            } catch (error) {
              console.warn('load standalone prompt locations failed', error);
              if (typeof onLocationError === 'function') onLocationError(error);
            }
          })();
        },
      },
      nsfw: {
        type: 'tags',
        getTags: async () => {
          await loadNsfwKeywords();
          return getNsfwKeywords();
        },
      },
    },
  });
};
